import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import useIsUiHidden from 'hooks/dom/useIsUiHidden';
import { negativeDataVmi } from 'store/entities/professionalReport/selectors/negativeDataSelectors';

import TableColumnDate from 'components/reusable/TableColumnDate';
import ComposedChart from 'components/reusable/graphs/ComposedChart';

import { Col, Row, Typography, Card } from 'antd';
import { ReactComponent as TaxArrearsicon } from 'assets/img/icons/cardIcons/taxArrears.svg';
import ReportBlockHeader from '../../../components/reusable/report/ReportBlockHeader';
import useFormatDate from '../../../hooks/format/useFormatDate';
import useNumberFormatter from '../../../hooks/format/useNumberFormatter';
import CollapsibleCard from '../../../components/reusable/CollapsibleCard';
import ProfTable from '../../../components/reusable/ProfTable';

const { Paragraph } = Typography;

const VMIDebtInformation = () => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const { vmiDebt, vmiDebtsHistory = [] } = useSelector(negativeDataVmi);
  const formatNumber = useNumberFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const isUiHidden = useIsUiHidden();

  const sortedData = useMemo(() => {
    return [...vmiDebtsHistory].sort((a, b) => a.dateFrom.localeCompare(b.dateFrom));
  }, [vmiDebtsHistory]);

  const debtColumns = [
    {
      title: t('professionalReport.negativeData.VmiPenaltiesInformation.dateFrom'),
      dataIndex: 'dateFrom',
      render: TableColumnDate,
      width: isUiHidden ? 80 : 120,
    },
    {
      title: t('professionalReport.negativeData.VmiPenaltiesInformation.dateTo'),
      dataIndex: 'dateTo',
      render: TableColumnDate,
      width: isUiHidden ? 80 : 120,
    },
    {
      title: t('professionalReport.negativeData.VmiPenaltiesInformation.amount'),
      dataIndex: 'amount',
      render: amount => (amount ? formatNumber(amount) : formatNumber(0)),
    },
    {
      title: t('professionalReport.negativeData.VmiPenaltiesInformation.rights'),
      dataIndex: 'deferredAmount',
      render: amount => (amount ? formatNumber(amount) : formatNumber(0)),
    },
  ];

  return (
    <Card
      className={`card-simple card-simple-padding-none space-vertical-sm has-shadow ${
        vmiDebt.arrear && vmiDebt.deferredArrear < vmiDebt.arrear ? 'negative-class' : ''
      }`}
      style={{ width: '100%', background: '#f4f4f4' }}
    >
      <div id="vmiPenalties" className="space-top-lg">
        <ReportBlockHeader
          icon={<TaxArrearsicon />}
          title={t('professionalReport.negativeData.VmiPenaltiesInformation.title')}
        />
      </div>
      {(vmiDebt?.arrear || vmiDebt?.deferredArrear || vmiDebt?.latestImportDate) && (
        <Paragraph>
          <Trans
            i18nKey={`professionalReport.negativeData.VmiPenaltiesInformation.description`}
            values={{
              arrear: vmiDebt?.arrear || '-',
              deferredArrear: vmiDebt?.deferredArrear || '-',
              latestImportDate: formatDate(vmiDebt?.latestImportDate) || '-',
            }}
          />
        </Paragraph>
      )}
      {vmiDebtsHistory.length ? (
        <CollapsibleCard title={t('global.detailedInformation')}>
          <ProfTable
            bordered
            dataSource={vmiDebtsHistory}
            columns={debtColumns}
            scroll={{ x: 'max-content' }}
            pagination={isUiHidden ? false : { defaultPageSize: 10, showSizeChanger: true }}
          />
        </CollapsibleCard>
      ) : null}
      <>
        {!!sortedData?.length ? (
          <Row className="space-vertical-sm">
            <Col>
              <Trans
                i18nKey="professionalReport.negativeData.VmiPenaltiesInformation.noNewData"
                components={{ strong: <strong /> }}
              />
            </Col>{' '}
          </Row>
        ) : null}
        <Row
          className="space-vertical-md no-break"
          style={{ pageBreakInside: 'avoid', pageBreakAfter: 'auto', background: 'white' }}
        >
          <Col span={24} style={{ padding: 16 }}>
            <ComposedChart
              id="vmiPenalties"
              series={[
                {
                  type: 'line',
                  dataKey: 'amount',
                  stroke: '#C40F11',
                  key: 'amount',
                },
                {
                  type: 'line',
                  dataKey: 'deferredAmount',
                  stroke: '#999999',
                  key: 'deferredAmount',
                },
              ]}
              data={sortedData}
              lazyLoaded
              xAxisDataKey="dateFrom"
              useValueFormatter
              noInformationText="professionalReport.negativeData.VmiPenaltiesInformation.noInformation"
              updateDate
              hideLabels
              noValueDots
            />
          </Col>
        </Row>
      </>
    </Card>
  );
};

export default VMIDebtInformation;
